import type {
  AdmissionSettings,
  ChangeRequestDto,
  ChangeRequestInput,
  FamilyApprovalResultDto,
  FamilyApprovalsInput,
  FamilyInviteBatchInput,
  FamilyInviteResultDto,
  FamilyQueueItemDto,
  InviteCandidatesDto,
  InviteCandidatesQuery,
  PersonInput,
  PortalInviteInput,
  PortalInviteResult,
  StudentProfileDto,
  TablePage,
} from "@eduleo/shared";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSessionKey } from "../../app/session-sync";
import type { TableParams } from "../../hooks/use-table-params";
import { apiRequest } from "../../lib/api-client";
import { studentKeys } from "../students/students-api";

/** The family portal: the welcome page, the office's invite and decisions, change requests. */

const seg = encodeURIComponent;

export const portalKeys = {
  onboarding: (session: string | null) => ["portal", session, "onboarding"] as const,
  queue: (session: string | null, query: Record<string, unknown>) =>
    ["portal", session, "queue", query] as const,
  candidates: (session: string | null, query: InviteCandidatesQuery) =>
    ["portal", session, "candidates", query] as const,
  changes: (session: string | null, id: string) => ["portal", session, "changes", id] as const,
};

export interface Onboarding {
  settings: AdmissionSettings;
  students: StudentProfileDto[];
}

export function useOnboarding() {
  const session = useSessionKey();
  return useQuery({
    queryKey: portalKeys.onboarding(session),
    queryFn: ({ signal }) => apiRequest<Onboarding>("/portal/onboarding", { signal }),
    enabled: Boolean(session),
  });
}

/** Changes on the welcome page; each answer replaces the student on it. */
export type FamilyChange =
  | {
      kind: "details";
      details: Record<string, unknown>;
      custom: Record<string, unknown>;
      version: number;
    }
  | { kind: "person"; person: PersonInput }
  | { kind: "photo"; file: File }
  | { kind: "document"; documentKind: string; file: File }
  | { kind: "remove-document"; documentId: string; version: number }
  | { kind: "submit"; version: number };

function fileForm(file: File, kind?: string): FormData {
  const form = new FormData();
  form.set("file", file, file.name);
  if (kind) form.set("kind", kind);
  return form;
}

export function useFamilyChange(studentId: string) {
  const queryClient = useQueryClient();
  const session = useSessionKey();
  const base = `/portal/onboarding/students/${seg(studentId)}`;
  return useMutation({
    mutationFn: (change: FamilyChange): Promise<unknown> => {
      switch (change.kind) {
        case "details":
          return apiRequest(base, {
            method: "PATCH",
            body: { details: change.details, custom: change.custom, version: change.version },
          });
        case "person":
          return apiRequest(`${base}/people`, { method: "POST", body: change.person });
        case "photo":
          return apiRequest(`${base}/photo`, { method: "PUT", body: fileForm(change.file) });
        case "document":
          return apiRequest(`${base}/documents`, {
            method: "POST",
            body: fileForm(change.file, change.documentKind),
          });
        case "remove-document":
          return apiRequest(`${base}/documents/${seg(change.documentId)}`, {
            method: "DELETE",
            query: { version: change.version },
          });
        case "submit":
          return apiRequest(`${base}/submit`, {
            method: "POST",
            body: { version: change.version },
          });
      }
    },
    // Stays pending until the page has the new state, so nothing shows as saved too early.
    onSettled: () => queryClient.invalidateQueries({ queryKey: portalKeys.onboarding(session) }),
  });
}

export function useInvite(studentId: string) {
  const queryClient = useQueryClient();
  const session = useSessionKey();
  return useMutation({
    mutationFn: (input: PortalInviteInput) =>
      apiRequest<PortalInviteResult>(`/portal/students/${seg(studentId)}/invite`, {
        method: "POST",
        body: input,
      }),
    // The new sign-ins show at once; the student's page reloads in the background.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: studentKeys.one(session, studentId) });
    },
  });
}

export function useRemoveLogin(studentId: string) {
  const queryClient = useQueryClient();
  const session = useSessionKey();
  return useMutation({
    mutationFn: (userId: string) =>
      apiRequest<void>(`/portal/students/${seg(studentId)}/logins/${seg(userId)}`, {
        method: "DELETE",
      }),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: studentKeys.one(session, studentId) }),
  });
}

export function useReviewSubmission(studentId: string) {
  const queryClient = useQueryClient();
  const session = useSessionKey();
  return useMutation({
    mutationFn: (body: { decision: "approve" | "send_back"; note?: string; version: number }) =>
      apiRequest<{ emailSent: boolean | null }>(`/portal/students/${seg(studentId)}/review`, {
        method: "POST",
        body,
      }),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: studentKeys.one(session, studentId) }),
        queryClient.invalidateQueries({ queryKey: studentKeys.history(session, studentId) }),
      ]),
  });
}

export function useChangeRequests(studentId: string, enabled: boolean) {
  const session = useSessionKey();
  return useQuery({
    queryKey: portalKeys.changes(session, studentId),
    queryFn: async ({ signal }) =>
      (
        await apiRequest<{ items: ChangeRequestDto[] }>(
          `/portal/students/${seg(studentId)}/changes`,
          { signal },
        )
      ).items,
    enabled: Boolean(session) && enabled,
  });
}

export function useRequestChange(studentId: string) {
  const queryClient = useQueryClient();
  const session = useSessionKey();
  return useMutation({
    mutationFn: (body: ChangeRequestInput) =>
      apiRequest<ChangeRequestDto>(`/portal/students/${seg(studentId)}/changes`, {
        method: "POST",
        body,
      }),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: portalKeys.changes(session, studentId) }),
        queryClient.invalidateQueries({ queryKey: studentKeys.one(session, studentId) }),
      ]),
  });
}

export function useReviewChange(studentId: string) {
  const queryClient = useQueryClient();
  const session = useSessionKey();
  return useMutation({
    mutationFn: ({
      changeId,
      ...body
    }: {
      changeId: string;
      decision: "approved" | "rejected";
      note?: string;
      version: number;
    }) =>
      apiRequest<ChangeRequestDto>(
        `/portal/students/${seg(studentId)}/changes/${seg(changeId)}/review`,
        { method: "POST", body },
      ),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: portalKeys.changes(session, studentId) }),
        queryClient.invalidateQueries({ queryKey: studentKeys.one(session, studentId) }),
        queryClient.invalidateQueries({ queryKey: studentKeys.history(session, studentId) }),
      ]),
  });
}

/** Students the office may invite families for, by class and family app status. */
export function useInviteCandidates(query: InviteCandidatesQuery) {
  const session = useSessionKey();
  return useQuery({
    queryKey: portalKeys.candidates(session, query),
    queryFn: ({ signal }) =>
      apiRequest<InviteCandidatesDto>("/portal/invite-candidates", {
        signal,
        query: { sectionId: query.sectionId, portal: query.portal },
      }),
    enabled: Boolean(session),
  });
}

/** One batch of a bulk invite (at most 20 students). */
export function inviteBatch(body: FamilyInviteBatchInput) {
  return apiRequest<{ items: FamilyInviteResultDto[] }>("/portal/invites", {
    method: "POST",
    body,
  });
}

/** The office's queue: families waiting for the school, or still completing. */
export function useFamilyQueue(params: TableParams) {
  const session = useSessionKey();
  const query = {
    q: params.q,
    status: params.filters.status || "submitted",
    sectionId: params.filters.sectionId,
    sort: params.sort,
    limit: params.pageSize,
    cursor: params.cursor,
  };
  return useQuery({
    queryKey: portalKeys.queue(session, query),
    queryFn: ({ signal }) =>
      apiRequest<TablePage<FamilyQueueItemDto>>("/portal/families", { signal, query }),
    placeholderData: keepPreviousData,
    enabled: Boolean(session),
  });
}

/** How many families are waiting for the school (for the home page). */
export function useFamiliesWaiting(enabled: boolean) {
  const session = useSessionKey();
  return useQuery({
    queryKey: portalKeys.queue(session, { count: true }),
    queryFn: async ({ signal }) =>
      (
        await apiRequest<TablePage<FamilyQueueItemDto>>("/portal/families", {
          signal,
          query: { limit: 1 },
        })
      ).total,
    enabled: Boolean(session) && enabled,
  });
}

export function useApproveFamilies() {
  const queryClient = useQueryClient();
  return useMutation({
    // The API takes 50 at a time; a page of 100 goes in two.
    mutationFn: async (body: FamilyApprovalsInput) => {
      const items: FamilyApprovalResultDto[] = [];
      for (let start = 0; start < body.items.length; start += 50) {
        const answer = await apiRequest<{ items: FamilyApprovalResultDto[] }>("/portal/approvals", {
          method: "POST",
          body: { items: body.items.slice(start, start + 50) },
        });
        items.push(...answer.items);
      }
      return { items };
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["portal"] });
      void queryClient.invalidateQueries({ queryKey: studentKeys.all });
    },
  });
}
